import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import { ApiService, SentryService } from '../../services';
import {
  buildNewInsurancePolicyRequestData,
  Insurance,
  InsuranceStatus,
  mapInsuranceCard,
  mapPatientInsurance,
  PatientInsurance,
} from '../../models';
import {
  CcsSentryError,
  ApiStoreData,
  defaultApiStoreIterableData,
  MissingApiRequestDataError,
  defaultApiStoreData,
} from '../utils';
import { sortBy } from 'lodash-es';
import {
  CreatePatientInsurance,
  FetchPatientInsuranceCards,
  FetchPatientInsurances,
} from './patient-insurances.actions';
import { LookupState } from '../lookup/lookup.state';
import { PatientState } from '../patient/patient.state';

export interface PatientInsurancesStateModel {
  insurance: ApiStoreData<PatientInsurance | null>;
  insuranceCards: ApiStoreData<Insurance[]>;
  createInsurance: ApiStoreData<null>;
}

@State<PatientInsurancesStateModel>({
  name: 'patientInsurances',
  defaults: {
    insurance: defaultApiStoreData,
    insuranceCards: defaultApiStoreIterableData,
    createInsurance: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class PatientInsurancesState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static patientInsurance(state: PatientInsurancesStateModel): PatientInsurance | null {
    return state.insurance.data;
  }

  @Selector()
  static isFinished(state: PatientInsurancesStateModel): boolean {
    return state.insurance.isFinished;
  }

  @Selector()
  static isLoading(state: PatientInsurancesStateModel): boolean {
    return state.insurance.isLoading || !state.insurance.isFinished;
  }

  @Selector()
  static error(state: PatientInsurancesStateModel): CcsSentryError | null {
    return state.insurance.error;
  }

  @Selector()
  static patientInsuranceCardsState(state: PatientInsurancesStateModel): ApiStoreData<Insurance[]> {
    return state.insuranceCards;
  }

  @Selector()
  static createInsuranceState(state: PatientInsurancesStateModel): ApiStoreData<null> {
    return state.createInsurance;
  }

  fetchPatientInsurance(ctx: StateContext<PatientInsurancesStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.insurance.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const insuranceGroups = this.store.selectSnapshot(LookupState.insuranceGroups);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'insurance' });
    }

    return this.apiService.getPatientInsurance(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapPatientInsurance(response.data[0], insuranceGroups);
          this.handleSuccess({ ctx, data, stateKey: 'insurance' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'insurance' }))
    );
  }

  fetchPatientInsuranceCards(ctx: StateContext<PatientInsurancesStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.insuranceCards.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'insuranceCards' });
    }

    return this.apiService.getPatientInsuranceCards(patientId).pipe(
      tap({
        next: (response) => {
          const data = sortBy(
            response.data
              .map(mapInsuranceCard)
              .filter((ic) => ic.status === InsuranceStatus.Active || ic.status === InsuranceStatus.Pending),
            ['type', 'status']
          );

          this.handleSuccess({ ctx, data, stateKey: 'insuranceCards' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'insuranceCards' }))
    );
  }

  addPatientInsurance(ctx: StateContext<PatientInsurancesStateModel>, action: CreatePatientInsurance) {
    ctx.setState(
      produce((draft) => {
        draft.createInsurance.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'createInsurance' });
    }

    const body = buildNewInsurancePolicyRequestData(action.formValues);

    return this.apiService.createPatientInsurance(patientId, [body]).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'createInsurance' });
          this.store.dispatch(new FetchPatientInsuranceCards());
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'createInsurance', resetData: false })
      )
    );
  }

  @Action(FetchPatientInsurances)
  fetchInsurances(ctx: StateContext<PatientInsurancesStateModel>) {
    return this.fetchPatientInsurance(ctx);
  }

  @Action(FetchPatientInsuranceCards)
  fetchInsuranceCards(ctx: StateContext<PatientInsurancesStateModel>) {
    return this.fetchPatientInsuranceCards(ctx);
  }

  @Action(CreatePatientInsurance)
  createPatientInsurance(ctx: StateContext<PatientInsurancesStateModel>, action: CreatePatientInsurance) {
    return this.addPatientInsurance(ctx, action);
  }
}
